import React, { useState, useEffect } from 'react';
import type { IFrameWidgetConfig } from './IFrameWidget';
import { AlertTriangle } from 'lucide-react';

interface IFrameWidgetConfigProps {
  initialConfig: Partial<IFrameWidgetConfig>;
  onChange: (config: IFrameWidgetConfig) => void;
}

// Extract src from iframe HTML code
function extractIframeSrc(input: string): string | null {
  if (input.includes('<iframe')) {
    const srcMatch = input.match(/src=["']([^"']+)["']/);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }
  }
  return input;
}

// Validate URL format
function isValidUrl(urlString: string): boolean {
  if (!urlString) return false;

  // Extract URL from iframe code if needed
  const url = extractIframeSrc(urlString);
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

export function IFrameWidgetConfigForm({ initialConfig = {}, onChange }: IFrameWidgetConfigProps) {
  const [url, setUrl] = useState(initialConfig?.url || '');
  const [backgroundColor, setBackgroundColor] = useState(initialConfig?.backgroundColor || '');
  const [urlError, setUrlError] = useState('');

  // Validate URL on change
  useEffect(() => {
    if (url && !isValidUrl(url)) {
      setUrlError('Please enter a valid URL (http:// or https://)');
    } else {
      setUrlError('');
    }
  }, [url]);

  // Live update on every change
  useEffect(() => {
    // Only update if URL is valid or empty
    if (!url || isValidUrl(url)) {
      const config: IFrameWidgetConfig = {
        url: url || '',
        backgroundColor: backgroundColor || undefined,
      };
      onChange(config);
    }
  }, [url, backgroundColor]);

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor('')}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                title="Clear background"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* URL or IFrame Code Input */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          URL or IFrame Code *
        </label>
        <textarea
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com or paste <iframe src=...></iframe>"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono resize-none"
        />
        {urlError && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{urlError}</p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Paste a URL or full iframe embed code
        </p>
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
              Security Notice
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">
              Only embed trusted websites. Some sites may not allow embedding due to security policies.
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>Tip:</strong> Test URLs that work well for embedding:
        </p>
        <ul className="text-xs text-blue-600 dark:text-blue-400 mt-2 space-y-1 ml-4">
          <li>• https://example.com</li>
          <li>• https://www.wikipedia.org</li>
          <li>• Internal company dashboards</li>
        </ul>
      </div>
    </div>
  );
}
